import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { toast } from "sonner";
import type { AxiosError } from "axios";

import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/marketing/components/ui/dialog";
import type { Brand, CreateBrandRequest, UpdateBrandRequest } from "@/marketing/models/brand";
import {
  BRAND_HEX_COLOR_PATTERN,
  BRAND_ORGANIZATION_ID_MAX_LENGTH,
  BRAND_SLUG_PATTERN,
} from "@/marketing/models/brand";
import { brandAPIClient } from "@/marketing/infrastructure/influencer/brand/brandAPIClient";
import { BRAND_KEYS } from "@/marketing/infrastructure/influencer/brand/utils/keys";
import { ImageUploadField } from "@/marketing/components/influencerCampaign/ImageUploadField";

const URL_PATTERN = /^https?:\/\/.+/;

function isValidUrl(value: string): boolean {
  try {
    new URL(value);
    return URL_PATTERN.test(value);
  } catch {
    return false;
  }
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-foreground/80">
        {label}
        {required && <span className="tw-text-red-500"> *</span>}
      </label>
      {children}
      {error && <p className="tw-mt-1 tw-text-[11px] tw-text-red-500">{error}</p>}
    </div>
  );
}

interface UrlChipInputProps {
  values: string[];
  inputValue: string;
  error: string;
  placeholder?: string;
  onInputChange: (value: string) => void;
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}

function UrlChipInput({
  values,
  inputValue,
  error,
  placeholder,
  onInputChange,
  onRemove,
  onPaste,
  onKeyDown,
  inputRef,
}: UrlChipInputProps) {
  return (
    <div
      className="tw-flex tw-min-h-[38px] tw-flex-wrap tw-items-center tw-gap-1.5 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-2 tw-py-1.5 tw-cursor-text"
      onClick={() => inputRef.current?.focus()}
    >
      {values.map((url) => (
        <span
          key={url}
          className="tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-border tw-bg-muted tw-px-2 tw-py-0.5 tw-text-xs tw-max-w-[240px]"
        >
          <span className="tw-truncate">{url}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(url);
            }}
            className="tw-ml-0.5 tw-flex-shrink-0 tw-text-muted-foreground hover:tw-text-foreground"
          >
            <X className="tw-h-3 tw-w-3" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        value={inputValue}
        onChange={(e) => onInputChange(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        placeholder={values.length === 0 ? placeholder : ""}
        className="tw-min-w-[200px] tw-flex-1 tw-bg-transparent tw-text-sm tw-outline-none placeholder:tw-text-muted-foreground"
      />
      {error && <p className="tw-mt-1 tw-w-full tw-text-[11px] tw-text-red-500">{error}</p>}
    </div>
  );
}

interface BrandFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  initialBrand?: Brand | undefined;
}

export function BrandFormDialog({
  open,
  onOpenChange,
  mode,
  initialBrand,
}: BrandFormDialogProps) {
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [organizationId, setOrganizationId] = useState("");

  const [brandAssetsLinks, setBrandAssetsLinks] = useState<string[]>([]);
  const [assetsInput, setAssetsInput] = useState("");
  const [assetsError, setAssetsError] = useState("");
  const assetsInputRef = useRef<HTMLInputElement>(null);

  const [productLinks, setProductLinks] = useState<string[]>([]);
  const [productInput, setProductInput] = useState("");
  const [productError, setProductError] = useState("");
  const productInputRef = useRef<HTMLInputElement>(null);

  const [brandColors, setBrandColors] = useState<string[]>([]);
  const [colorInput, setColorInput] = useState("");
  const [colorError, setColorError] = useState("");
  const [slugError, setSlugError] = useState("");

  const colorInputRef = useRef<HTMLInputElement>(null);

  function resetForm() {
    setName("");
    setSlug("");
    setDescription("");
    setLogoUrl("");
    setWebsiteUrl("");
    setOrganizationId("");
    setBrandAssetsLinks([]);
    setAssetsInput("");
    setAssetsError("");
    setProductLinks([]);
    setProductInput("");
    setProductError("");
    setBrandColors([]);
    setColorInput("");
    setColorError("");
    setSlugError("");
  }

  useEffect(() => {
    if (mode === "edit" && initialBrand) {
      setName(initialBrand.name);
      setSlug(initialBrand.slug ?? "");
      setDescription(initialBrand.description ?? "");
      setLogoUrl(initialBrand.logoUrl ?? "");
      setWebsiteUrl(initialBrand.websiteUrl ?? "");
      setOrganizationId(initialBrand.organizationId ?? "");
      setBrandAssetsLinks(initialBrand.brandAssetsLinks ?? []);
      setAssetsInput("");
      setAssetsError("");
      setProductLinks(initialBrand.productLinks ?? []);
      setProductInput("");
      setProductError("");
      setBrandColors(initialBrand.brandColors ?? []);
      setColorInput("");
      setColorError("");
      setSlugError("");
    } else if (mode === "create" && open) {
      resetForm();
    }
  }, [open, mode, initialBrand]);

  function handleSlugChange(value: string) {
    setSlug(value);
    if (value && !BRAND_SLUG_PATTERN.test(value)) {
      setSlugError("Lowercase letters, numbers, and dashes only");
    } else {
      setSlugError("");
    }
  }

  // ── URL chip helpers ──────────────────────────────────────────────────────

  function addUrlChip(
    raw: string,
    existing: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    setInput: React.Dispatch<React.SetStateAction<string>>,
    setError: React.Dispatch<React.SetStateAction<string>>,
  ) {
    const value = raw.trim();
    if (!value) return;
    if (!isValidUrl(value)) {
      setError(`"${value}" is not a valid URL`);
      return;
    }
    if (existing.includes(value)) {
      setError("URL already added");
      return;
    }
    if (existing.length >= 20) {
      setError("Maximum 20 URLs");
      return;
    }
    setList((prev) => [...prev, value]);
    setInput("");
    setError("");
  }

  function makeKeyDownHandler(
    input: string,
    existing: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    setInput: React.Dispatch<React.SetStateAction<string>>,
    setError: React.Dispatch<React.SetStateAction<string>>,
  ) {
    return (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        addUrlChip(input, existing, setList, setInput, setError);
      }
    };
  }

  function makePasteHandler(
    existing: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    setInput: React.Dispatch<React.SetStateAction<string>>,
    setError: React.Dispatch<React.SetStateAction<string>>,
  ) {
    return (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData("text");
      const values = pasted.split(/[\s,]+/).filter(Boolean);
      const first = values[0];
      if (values.length === 1 && first !== undefined) {
        setInput(first);
        addUrlChip(first, existing, setList, setInput, setError);
      } else {
        for (const v of values) {
          addUrlChip(v, existing, setList, setInput, setError);
        }
      }
    };
  }

  // ── Color chip helpers ────────────────────────────────────────────────────

  function addColor(raw: string) {
    const value = raw.trim();
    if (!value) return;
    if (!BRAND_HEX_COLOR_PATTERN.test(value)) {
      setColorError(`"${value}" is not a valid hex color`);
      return;
    }
    if (brandColors.includes(value)) {
      setColorError("Color already added");
      return;
    }
    if (brandColors.length >= 20) {
      setColorError("Maximum 20 colors");
      return;
    }
    setBrandColors((prev) => [...prev, value]);
    setColorInput("");
    setColorError("");
  }

  function handleColorKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addColor(colorInput);
    }
  }

  function handleColorPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    const values = pasted.split(/[\s,]+/).filter(Boolean);
    const first = values[0];
    if (values.length === 1 && first !== undefined) {
      setColorInput(first);
      addColor(first);
    } else {
      for (const v of values) addColor(v);
    }
  }

  function removeColor(color: string) {
    setBrandColors((prev) => prev.filter((c) => c !== color));
  }

  // ── Mutation helpers ──────────────────────────────────────────────────────

  function buildErrorMessage(error: unknown): string {
    const axiosError = error as AxiosError<{ message?: string | string[] }>;
    const msg = axiosError?.response?.data?.message;
    if (Array.isArray(msg)) return msg.join(", ");
    if (typeof msg === "string") return msg;
    return "Failed to save brand";
  }

  const createMutation = useMutation({
    mutationFn: (request: CreateBrandRequest) =>
      brandAPIClient.createBrand(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRAND_KEYS.lists() });
      onOpenChange(false);
      resetForm();
      toast.success("Brand created");
    },
    onError: (error) => {
      toast.error(buildErrorMessage(error));
    },
  });

  const editMutation = useMutation({
    mutationFn: (request: UpdateBrandRequest) => {
      if (!initialBrand)
        return Promise.reject(new Error("Cannot edit without initialBrand"));
      return brandAPIClient.updateBrand(initialBrand.brandId, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRAND_KEYS.lists() });
      if (initialBrand) {
        queryClient.invalidateQueries({
          queryKey: BRAND_KEYS.detail(initialBrand.brandId),
        });
      }
      onOpenChange(false);
      toast.success("Brand updated");
    },
    onError: (error) => {
      toast.error(buildErrorMessage(error));
    },
  });

  const isPending = createMutation.isPending || editMutation.isPending;

  const isValid =
    name.trim().length > 0 &&
    slugError === "" &&
    colorError === "" &&
    assetsError === "" &&
    productError === "";

  function submit() {
    if (!isValid) return;

    const slugVal = slug.trim();
    const descVal = description.trim();
    const logoVal = logoUrl.trim();
    const websiteVal = websiteUrl.trim();
    const organizationIdVal = organizationId.trim();

    if (mode === "edit") {
      const request: UpdateBrandRequest = { name: name.trim() };
      if (slugVal) request.slug = slugVal;
      if (descVal) request.description = descVal;
      if (logoVal) request.logoUrl = logoVal;
      if (websiteVal) request.websiteUrl = websiteVal;
      if (brandAssetsLinks.length > 0) request.brandAssetsLinks = brandAssetsLinks;
      if (productLinks.length > 0) request.productLinks = productLinks;
      if (brandColors.length > 0) request.brandColors = brandColors;
      if (organizationIdVal) request.organizationId = organizationIdVal;
      // Only a brand that was linked needs null; others omit it so older Facades accept the edit.
      else if (initialBrand?.organizationId) request.organizationId = null;
      editMutation.mutate(request);
    } else {
      const request: CreateBrandRequest = { name: name.trim() };
      if (slugVal) request.slug = slugVal;
      if (descVal) request.description = descVal;
      if (logoVal) request.logoUrl = logoVal;
      if (websiteVal) request.websiteUrl = websiteVal;
      if (brandAssetsLinks.length > 0) request.brandAssetsLinks = brandAssetsLinks;
      if (productLinks.length > 0) request.productLinks = productLinks;
      if (brandColors.length > 0) request.brandColors = brandColors;
      if (organizationIdVal) request.organizationId = organizationIdVal;
      createMutation.mutate(request);
    }
  }

  const isEdit = mode === "edit";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw-max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Brand" : "New Brand"}</DialogTitle>
          <p className="tw-text-sm tw-text-muted-foreground">
            {isEdit ? "Update brand details" : "Add a new brand to the catalog"}
          </p>
        </DialogHeader>
        <div className="tw-grid tw-gap-4 tw-py-2">
          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <Field label="Brand name" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Reboncel"
                maxLength={255}
              />
            </Field>
            <Field label="Slug" error={slugError}>
              <Input
                value={slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder="e.g. reboncel"
                maxLength={128}
              />
              {!slugError && (
                <p className="tw-mt-1 tw-text-[11px] tw-text-muted-foreground">
                  Lowercase letters, numbers, dashes only
                </p>
              )}
            </Field>
          </div>
          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={2048}
              placeholder="Short description of the brand..."
              className="tw-min-h-[72px] tw-w-full tw-resize-y tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm"
            />
          </Field>
          <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-items-start">
            <div>
              <ImageUploadField
                label="Logo"
                {...(logoUrl ? { value: logoUrl } : {})}
                onChange={(url) => setLogoUrl(url ?? "")}
              />
              <Input
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="or paste an image URL"
                aria-label="Logo URL"
                maxLength={2048}
                className="tw-mt-2 tw-h-9"
              />
            </div>
            <Field label="Website URL">
              <Input
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://..."
                maxLength={2048}
              />
            </Field>
          </div>
          <Field label="Business Portal organization ID">
            <Input
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              placeholder="e.g. abc123xyz"
              aria-label="Business Portal organization ID"
              maxLength={BRAND_ORGANIZATION_ID_MAX_LENGTH}
              className="tw-font-mono"
            />
            <p className="tw-mt-1 tw-text-[11px] tw-text-muted-foreground">
              Members of this Business Portal company can review this brand&apos;s
              Performance Seeding candidates. Leave empty if the brand has no
              portal account.
            </p>
          </Field>
          <Field label="Brand Assets Links" error={assetsError}>
            <UrlChipInput
              values={brandAssetsLinks}
              inputValue={assetsInput}
              error={assetsError}
              placeholder="https://drive.google.com/… — press Enter or comma to add"
              onInputChange={(v) => { setAssetsInput(v); setAssetsError(""); }}
              onAdd={(v) => addUrlChip(v, brandAssetsLinks, setBrandAssetsLinks, setAssetsInput, setAssetsError)}
              onRemove={(v) => setBrandAssetsLinks((prev) => prev.filter((u) => u !== v))}
              onKeyDown={makeKeyDownHandler(assetsInput, brandAssetsLinks, setBrandAssetsLinks, setAssetsInput, setAssetsError)}
              onPaste={makePasteHandler(brandAssetsLinks, setBrandAssetsLinks, setAssetsInput, setAssetsError)}
              inputRef={assetsInputRef}
            />
            {!assetsError && (
              <p className="tw-mt-1 tw-text-[11px] tw-text-muted-foreground">
                Drive, Notion, etc. Press Enter or comma to add. Paste multiple URLs separated by spaces or commas.
              </p>
            )}
          </Field>
          <Field label="Product Links" error={productError}>
            <UrlChipInput
              values={productLinks}
              inputValue={productInput}
              error={productError}
              placeholder="https://... — press Enter or comma to add"
              onInputChange={(v) => { setProductInput(v); setProductError(""); }}
              onAdd={(v) => addUrlChip(v, productLinks, setProductLinks, setProductInput, setProductError)}
              onRemove={(v) => setProductLinks((prev) => prev.filter((u) => u !== v))}
              onKeyDown={makeKeyDownHandler(productInput, productLinks, setProductLinks, setProductInput, setProductError)}
              onPaste={makePasteHandler(productLinks, setProductLinks, setProductInput, setProductError)}
              inputRef={productInputRef}
            />
            {!productError && (
              <p className="tw-mt-1 tw-text-[11px] tw-text-muted-foreground">
                Product page URLs. Press Enter or comma to add.
              </p>
            )}
          </Field>
          <Field label="Brand Colors" error={colorError}>
            <div
              className="tw-flex tw-min-h-[38px] tw-flex-wrap tw-items-center tw-gap-1.5 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-2 tw-py-1.5 tw-cursor-text"
              onClick={() => colorInputRef.current?.focus()}
            >
              {brandColors.map((color) => (
                <span
                  key={color}
                  className="tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-border tw-bg-muted tw-px-2 tw-py-0.5 tw-text-xs tw-font-mono"
                >
                  <span
                    className="tw-h-3 tw-w-3 tw-rounded-full tw-border tw-border-border tw-flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  {color}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeColor(color);
                    }}
                    className="tw-ml-0.5 tw-text-muted-foreground hover:tw-text-foreground"
                  >
                    <X className="tw-h-3 tw-w-3" />
                  </button>
                </span>
              ))}
              <input
                ref={colorInputRef}
                value={colorInput}
                onChange={(e) => {
                  setColorInput(e.target.value);
                  setColorError("");
                }}
                onKeyDown={handleColorKeyDown}
                onPaste={handleColorPaste}
                placeholder={brandColors.length === 0 ? "#ffffff or paste multiple" : ""}
                className="tw-min-w-[140px] tw-flex-1 tw-bg-transparent tw-text-sm tw-outline-none placeholder:tw-text-muted-foreground"
              />
            </div>
            {!colorError && (
              <p className="tw-mt-1 tw-text-[11px] tw-text-muted-foreground">
                Enter hex colors (e.g. #FF5733). Press Enter or comma to add.
              </p>
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={isPending || !isValid}>
            {isPending
              ? isEdit
                ? "Saving…"
                : "Creating…"
              : isEdit
                ? "Save changes"
                : "Create brand"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
